import { createContext, useContext, useEffect, useState } from "react";
import { toast } from "sonner";
import {
    vigilantes as initialVigilantes,
    rondas as initialRondas,
    ocorrencias as initialOcorrencias,
    emergencias as initialEmergencias,
    passagensTurno as initialPassagens,
    checklistPosto,
    pontosDeRonda,
    postos,
    condominios,
    getById,
} from "../data/mock";
import { supabase } from "../lib/supabase";
import { audit } from "../lib/audit";

const EMPRESA_ID = "ff438dc8-89fb-4d7c-9ea2-7e09162f242a";

const SCANS_KEY = "vigia.scans_qr";

const AppContext = createContext(null);

const STORAGE_KEY = "vigia.session.v2";

export function AppProvider({ children }) {
    const [user, setUser] = useState(() => {
        try {
            const raw = localStorage.getItem(STORAGE_KEY);
            return raw ? JSON.parse(raw) : null;
        } catch {
            return null;
        }
    });

    const [rondas, setRondas] = useState(initialRondas);
    const [ocorrencias, setOcorrencias] = useState(initialOcorrencias);
    const [emergencias, setEmergencias] = useState(initialEmergencias);
    const [passagens, setPassagens] = useState(initialPassagens);
    const [vigilantesList] = useState(initialVigilantes);

    const [scansQR, setScansQR] = useState(() => {
        try {
            const raw = localStorage.getItem(SCANS_KEY);
            return raw ? JSON.parse(raw) : [];
        } catch { return []; }
    });

    // Foto de perfil do vigilante logado
    const [fotoPerfilUrl, setFotoPerfilUrl] = useState(null);

    // Avisos não lidos
    const [avisosNaoLidos, setAvisosNaoLidos] = useState(0);

    useEffect(() => {
        if (!user?.id || user.role !== "vigilante") {
            setFotoPerfilUrl(null);
            return;
        }
        supabase
            .from("liberty_perfil_vigilante")
            .select("foto_url")
            .eq("vigilante_id", user.id)
            .maybeSingle()
            .then(({ data }) => setFotoPerfilUrl(data?.foto_url || null));
    }, [user?.id]); // eslint-disable-line

    // Busca contagem de avisos não lidos
    const calcAvisosNaoLidos = async (uid) => {
        const hoje = new Date(); hoje.setHours(0, 0, 0, 0);
        const [{ data: avData }, { data: leitData }] = await Promise.all([
            supabase.from("liberty_avisos").select("id, validade").eq("empresa_id", EMPRESA_ID).eq("status", "ativo"),
            supabase.from("liberty_avisos_leituras").select("aviso_id").eq("usuario_id", String(uid)),
        ]);
        if (!avData) return;
        const lidos = new Set((leitData || []).map((l) => l.aviso_id));
        const count = avData.filter((a) => {
            if (a.validade && new Date(a.validade + "T12:00:00") < hoje) return false;
            return !lidos.has(a.id);
        }).length;
        setAvisosNaoLidos(count);
    };

    useEffect(() => {
        if (!user) { setAvisosNaoLidos(0); return; }
        const uid = user?.usuarioId || user?.id;
        if (uid) calcAvisosNaoLidos(uid);
    }, [user?.id, user?.usuarioId]); // eslint-disable-line

    const marcarAvisoLido = () => {
        setAvisosNaoLidos((n) => Math.max(0, n - 1));
    };

    const recarregarAvisos = () => {
        const uid = user?.usuarioId || user?.id;
        if (uid) calcAvisosNaoLidos(uid);
    };

    // Checklist state per session (mock).
    const [checklistChecks, setChecklistChecks] = useState({});

    useEffect(() => {
        if (user) localStorage.setItem(STORAGE_KEY, JSON.stringify(user));
        else localStorage.removeItem(STORAGE_KEY);
    }, [user]);

    useEffect(() => {
        localStorage.setItem(SCANS_KEY, JSON.stringify(scansQR));
    }, [scansQR]);

    const login = ({ role, vigilanteId, usuarioId, usuarioNome, email }) => {
        if (role === "vigilante") {
            const v = vigilantesList.find((x) => x.id === vigilanteId) ||
                vigilantesList[0];
            setUser({
                role: "vigilante",
                id: v.id,
                usuarioId: usuarioId,
                nome: v.nome,
                matricula: v.matricula,
                postoId: v.postoId,
                condominioId: v.condominioId,
            });
        } else {
            setUser({ role, id: usuarioId, nome: usuarioNome, email });
        }
    };

    const logout = () => {
        audit({
            usuario_nome: user?.nome,
            usuario_role: user?.role,
            acao: "logout",
            modulo: "Acesso",
            descricao: `Logout: ${user?.nome || "desconhecido"}`,
        });
        setUser(null);
    };

    // Ronda actions (vigilante)
    const iniciarRonda = () => {
        if (!user || user.role !== "vigilante") return null;
        const novaRonda = {
            id: `r-${Date.now()}`,
            vigilanteId: user.id,
            postoId: user.postoId,
            inicio: new Date().toISOString(),
            fim: null,
            pontosVisitados: [],
            status: "em_andamento",
        };
        setRondas((r) => [novaRonda, ...r]);
        audit({
            usuario_nome: user?.nome,
            usuario_role: user?.role,
            acao: "ronda_iniciada",
            modulo: "Rondas",
            descricao: `Ronda iniciada por ${user?.nome} no posto ${user?.postoId || "—"}`,
        });
        return novaRonda;
    };

    const finalizarRonda = (rondaId) => {
        setRondas((r) =>
            r.map((x) =>
                x.id === rondaId
                    ? {
                          ...x,
                          fim: new Date().toISOString(),
                          status: "concluida",
                      }
                    : x,
            ),
        );
    };

    const registrarPontoRonda = (rondaId, pontoId) => {
        setRondas((r) =>
            r.map((x) =>
                x.id === rondaId
                    ? {
                          ...x,
                          pontosVisitados: x.pontosVisitados.includes(pontoId)
                              ? x.pontosVisitados
                              : [...x.pontosVisitados, pontoId],
                      }
                    : x,
            ),
        );
    };

    const rondaAtiva = rondas.find(
        (r) =>
            r.vigilanteId === user?.id && r.status === "em_andamento",
    );

    // Ocorrência
    const registrarOcorrencia = (data) => {
        const nova = {
            id: `oc-${Date.now()}`,
            vigilanteId: user?.id,
            postoId: user?.postoId,
            ...data,
            criadoEm: new Date().toISOString(),
            status: "pendente",
        };
        setOcorrencias((o) => [nova, ...o]);

        supabase.from("liberty_ocorrencias").insert({
            tipo: data.tipo || data.titulo || data.categoria || null,
            descricao: data.descricao || data.observacao || null,
            vigilante_id: user?.id,
            vigilante_nome: user?.nome || null,
            posto_id: user?.postoId || null,
            empresa_id: EMPRESA_ID,
            status: "pendente",
            created_at: nova.criadoEm,
        }).then(({ data: saved, error }) => {
            if (error) console.error("[Supabase] ocorrencia error:", error);
            else console.log("[Supabase] ocorrencia salva:", saved);
        });

        audit({
            usuario_nome: user?.nome,
            usuario_role: user?.role,
            acao: "ocorrencia_registrada",
            modulo: "Ocorrências",
            tabela: "liberty_ocorrencias",
            descricao: `Ocorrência registrada: ${data.tipo || "sem tipo"}`,
        });

        return nova;
    };

    // Emergência
    const dispararEmergencia = (observacao = "") => {
        const nova = {
            id: `em-${Date.now()}`,
            vigilanteId: user?.id,
            postoId: user?.postoId,
            criadoEm: new Date().toISOString(),
            status: "acionada",
            observacao,
        };
        setEmergencias((e) => [nova, ...e]);

        // Nota: alertas_panico não tem empresa_id nem observacao — apenas nome_vigilante e status
        supabase.from("alertas_panico").insert({
            nome_vigilante: user?.nome || null,
            status: "ativo",
            created_at: nova.criadoEm,
        }).then(({ error }) => {
            if (error) {
                console.error("[Supabase] alertas_panico ERRO:", error.code, error.message);
                console.error("[Supabase] detalhes:", error.details, "| hint:", error.hint);
            } else {
                console.log("[Supabase] alerta panico salvo com status 'ativo'");
            }
        });

        audit({
            usuario_nome: user?.nome,
            usuario_role: user?.role,
            acao: "emergencia_acionada",
            modulo: "Emergências",
            tabela: "alertas_panico",
            descricao: `Emergência acionada por ${user?.nome || "vigilante"}${observacao ? `: ${observacao}` : ""}`,
        });

        return nova;
    };

    // Passagem de turno
    const registrarPassagem = ({ paraVigilante, resumo, ...extra }) => {
        const dia = new Date();
        const inicioDia = new Date(dia.getFullYear(), dia.getMonth(), dia.getDate()).toISOString();
        const rondasNoTurno = rondas.filter(
            (r) => r.vigilanteId === user?.id && r.inicio >= inicioDia,
        ).length;
        const ocsNoTurno = ocorrencias.filter(
            (o) => o.vigilanteId === user?.id && o.criadoEm >= inicioDia,
        ).length;
        const nova = {
            id: `pt-${Date.now()}`,
            deVigilanteId: user?.id,
            paraVigilante,
            postoId: user?.postoId,
            criadoEm: new Date().toISOString(),
            resumo,
            rondasNoTurno,
            ocorrenciasNoTurno: ocsNoTurno,
            ...extra,
        };
        setPassagens((p) => [nova, ...p]);

        supabase.from("liberty_passagem_turno").insert({
            de_vigilante_id: user?.id,
            de_vigilante_nome: user?.nome || null,
            para_vigilante: paraVigilante || null,
            resumo: resumo || null,
            rondas_no_turno: rondasNoTurno,
            ocorrencias_no_turno: ocsNoTurno,
            posto_id: user?.postoId || null,
            empresa_id: EMPRESA_ID,
            created_at: nova.criadoEm,
        }).then(({ error }) => { if (error) console.error("[Supabase] passagem:", error.message); });

        audit({
            usuario_nome: user?.nome,
            usuario_role: user?.role,
            acao: "passagem_turno",
            modulo: "Passagem de Turno",
            tabela: "liberty_passagem_turno",
            descricao: `Passagem de ${user?.nome || "vigilante"} para ${paraVigilante}`,
        });

        return nova;
    };

    // QR scan
    const registrarScanQR = (pontoId, observacao = "", fotoUrl = null) => {
        if (!user) return null;
        const ponto = pontosDeRonda.find((p) => p.id === pontoId);
        const scan = {
            id: `scan-${Date.now()}`,
            pontoId,
            pontoNome: ponto?.nome || pontoId,
            vigilanteId: user.id,
            vigilanteNome: user.nome,
            observacao: observacao?.trim() || "",
            fotoUrl: fotoUrl || null,
            timestamp: new Date().toISOString(),
        };
        setScansQR((s) => [scan, ...s]);

        supabase.from("liberty_ronda_pontos").insert({
            empresa_id:    EMPRESA_ID,
            ronda_id:      null,
            ponto:         ponto?.nome || pontoId,
            iluminacao_ok: null,
            cameras_ok:    null,
            portao_ok:     null,
            area_limpa:    null,
            observacao:    observacao?.trim() || null,
            foto_url:      fotoUrl || null,
            validado_em:   scan.timestamp,
            vigilante_id:  user.id,
            vigilante_nome: user.nome,
        }).then(({ data, error }) => {
            if (error) {
                console.error("[Supabase] scan QR ERRO —", error.code, error.message);
                console.error("[Supabase] detalhes:", error.details, "| hint:", error.hint);
                toast.error("Ponto não registrado no servidor. Verifique sua conexão.", { duration: 6000 });
            } else {
                console.log("[Supabase] scan QR salvo com sucesso:", data);
            }
        });

        audit({
            usuario_nome: user?.nome,
            usuario_role: user?.role,
            acao: "scan_qr",
            modulo: "Rondas",
            tabela: "liberty_ronda_pontos",
            descricao: `Ponto confirmado: ${ponto?.nome || pontoId}${fotoUrl ? " (com foto)" : ""}`,
        });

        return scan;
    };

    // Checklist
    const toggleChecklist = (itemId) => {
        setChecklistChecks((s) => ({ ...s, [itemId]: !s[itemId] }));
    };

    const value = {
        user,
        login,
        logout,
        // Read-only
        vigilantes: vigilantesList,
        condominios,
        postos,
        pontosDeRonda,
        checklistPosto,
        // Mutable
        rondas,
        ocorrencias,
        emergencias,
        passagens,
        rondaAtiva,
        scansQR,
        // Actions
        iniciarRonda,
        finalizarRonda,
        registrarPontoRonda,
        registrarOcorrencia,
        dispararEmergencia,
        registrarPassagem,
        registrarScanQR,
        // Checklist
        checklistChecks,
        toggleChecklist,
        // Foto de perfil
        fotoPerfilUrl,
        setFotoPerfilUrl,
        // Avisos
        avisosNaoLidos,
        marcarAvisoLido,
        recarregarAvisos,
        // Helpers
        getById,
    };

    return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
}

export const useApp = () => {
    const ctx = useContext(AppContext);
    if (!ctx) throw new Error("useApp must be inside AppProvider");
    return ctx;
};
